/*
  Licensed under the Apache License, Version 2.0 (the "License"); you may not use
  this file except in compliance with the License. You may obtain a copy of the
  License at

      https://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software distributed
  under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
  CONDITIONS OF ANY KIND, either express or implied. See the License for the
  specific language governing permissions and limitations under the License.
*/

import type { Auth0Client, RedirectLoginOptions } from "@auth0/auth0-spa-js";

import SessionStorage from "./SessionStorage";
import { trackException } from "./Telemetry";

// Both apps hold a placeholder object until createAuth0Client resolves, so the
// client is typed as the one method used here, and partial at that.
type LoginCapableClient = Partial<Pick<Auth0Client, "loginWithRedirect">>;

// Set while a callback that failed to validate is being retried, so a callback
// that keeps failing stops instead of bouncing through Auth0 forever.
const CALLBACK_RETRY_KEY = "auth0-callback-retried";

// Module scope rather than React state: the guard has to be claimed and checked
// synchronously, and batched setState would let two callers through in the same
// tick - the race this exists to stop. Tests must reset the module to clear it.
let loginInFlight = false;

// Coming back from Auth0 with the browser's Back button restores this document
// from the bfcache instead of reloading it, so the guard is still set from the
// redirect that did happen. Release it, or every login button stays dead.
if (typeof window !== "undefined") {
  window.addEventListener("pageshow", (event: PageTransitionEvent) => {
    if (event.persisted) {
      loginInFlight = false;
    }
  });
}

/**
 * Send the user to Auth0.
 *
 * Auth0 keeps a single PKCE transaction per client id and buildAuthorizeUrl
 * writes it *after* awaiting sha256, so a second call overwrites the first
 * one's state while the browser keeps the navigation the first one started.
 * The callback then comes back carrying one state and gets validated against
 * the other, which fails as 'Invalid state' and silently signs the user out -
 * so only the first caller may redirect.
 *
 * Resolving is not proof the browser navigated - a call blocked by the guard,
 * or one made before the client exists, resolves immediately too - so callers
 * that hold a spinner or skip their fallback routing must check the result.
 *
 * @returns whether loginWithRedirect was actually called, and so whether a
 *  navigation to Auth0 is on its way.
 * @param onBeforeRedirect - runs only once the guard has been claimed, so a
 *  blocked second call can't clear app state without a redirect to follow it.
 */
export const startLogin = async (
  authClient: LoginCapableClient,
  {
    options = {},
    onBeforeRedirect,
  }: { options?: RedirectLoginOptions; onBeforeRedirect?: () => void } = {}
): Promise<boolean> => {
  // checked before the guard is claimed, so a caller that logs in once the
  // client has arrived isn't left blocked by an attempt that never redirected
  if (!authClient?.loginWithRedirect) {
    return false;
  }

  if (loginInFlight) {
    return false;
  }
  loginInFlight = true;

  try {
    onBeforeRedirect?.();

    const lang = SessionStorage.get("lang");

    await authClient.loginWithRedirect({
      ...(lang && { ui_locales: lang }),
      ...options,
    });
  } catch (e) {
    // a redirect that never happened shouldn't leave the button dead. try/catch
    // rather than .catch() so a synchronous throw releases the guard too.
    loginInFlight = false;
    throw e;
  }

  return true;
};

/**
 * Call on a callback that validated, so the next failure gets its own retry.
 */
export const clearCallbackRetry = () =>
  SessionStorage.remove(CALLBACK_RETRY_KEY);

/**
 * Report a handleRedirectCallback failure and, where it's worth it, re-issue
 * the login redirect.
 *
 * 'Invalid state' means the callback couldn't be matched to a pending
 * transaction. That can be user-driven (the callback URL reloaded, or login
 * finished in another tab) but it is also what a pair of racing login
 * redirects looks like, so it gets reported either way - tagged with whether
 * we recovered, to keep the two apart.
 *
 * @returns whether a retry redirect is on its way. False means the caller
 *  should fall back to its own routing - either because the failure isn't
 *  recoverable, or because the redirect couldn't be started.
 */
export const handleCallbackFailure = async (
  error: any,
  authClient: LoginCapableClient,
  { onBeforeRedirect }: { onBeforeRedirect?: () => void } = {}
): Promise<boolean> => {
  const isInvalidState = error?.message === "Invalid state";
  const willRetry = isInvalidState && !SessionStorage.get(CALLBACK_RETRY_KEY);

  trackException(error, {
    source: "auth0",
    stage: "handleRedirectCallback",
    // both tagged: 'recovered' alone can't tell a spent retry apart from any
    // other callback failure
    invalidState: isInvalidState,
    recovered: willRetry,
  });
  console.error(error);

  if (!willRetry) {
    return false;
  }

  // A state mismatch leaves the session at Auth0 untouched, so a fresh redirect
  // completes without prompting.
  SessionStorage.add(CALLBACK_RETRY_KEY, "true");

  let redirecting: boolean;
  try {
    redirecting = await startLogin(authClient, { onBeforeRedirect });
  } catch (e) {
    // a redirect that threw never left the page, so the retry wasn't spent
    SessionStorage.remove(CALLBACK_RETRY_KEY);
    throw e;
  }

  if (!redirecting) {
    // spend the one retry only on an attempt that actually left the page
    SessionStorage.remove(CALLBACK_RETRY_KEY);
  }

  return redirecting;
};
